import React from "react";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Box, Button, Tooltip } from "@mui/material";
import { useSlidingAppearance } from "../lib/AppearanceContext";
import { getHomePremium } from "../styles/homePremiumStyles";

const systemFont =
    '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", sans-serif';

function MiniPill({ label, selected, disabled, locked, onClick, tooltip, variant = "default" }) {
    const { tokens, isDark } = useSlidingAppearance();
    const home = getHomePremium(isDark);
    const premium = variant === "premium";
    const selectedBg = premium ? home.pillSelectedBg : tokens.miniPillSelectedBg;
    const selectedText = premium ? home.pillSelectedText : tokens.miniPillSelectedText;
    const inactiveBg = premium ? home.pillInactiveBg : tokens.miniPillInactiveBg;
    const inactiveText = premium ? home.pillInactiveText : tokens.miniPillInactiveText;
    const disabledColor = premium ? home.lockMuted : tokens.textSubtle;
    const inactiveBorder = isDark ? "rgba(255,255,255,0.1)" : "rgba(44, 58, 74, 0.06)";

    const button = (
        <Button
            type="button"
            disabled={disabled}
            onClick={onClick}
            aria-pressed={selected}
            sx={{
                minWidth: 0,
                minHeight: 30,
                px: 1.35,
                py: 0.3,
                borderRadius: "999px",
                textTransform: "none",
                fontSize: "0.75rem",
                fontWeight: selected ? 600 : 500,
                fontFamily: systemFont,
                bgcolor: selected ? selectedBg : inactiveBg,
                color: selected ? selectedText : inactiveText,
                border: premium
                    ? selected
                        ? `1px solid ${home.tealRing}`
                        : `1px solid ${inactiveBorder}`
                    : "none",
                boxShadow: "none",
                "&:hover": {
                    bgcolor: selected ? selectedBg : inactiveBg,
                    filter: "brightness(0.98)",
                },
                "&.Mui-disabled": {
                    color: disabledColor,
                },
            }}
        >
            <Box
                component="span"
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.35,
                }}
            >
                {label}
                {locked ? (
                    <LockOutlinedIcon
                        sx={{
                            fontSize: 13,
                            flexShrink: 0,
                            opacity: premium ? 1 : 0.55,
                            color: premium ? home.lockMuted : "inherit",
                        }}
                        aria-hidden
                    />
                ) : null}
            </Box>
        </Button>
    );

    if (tooltip && disabled) {
        return (
            <Tooltip title={tooltip} arrow placement="top">
                <Box component="span" sx={{ display: "inline-flex" }}>
                    {button}
                </Box>
            </Tooltip>
        );
    }

    return button;
}

/** Compact grid / level selectors inside a library browse card. */
export default function LibraryCardConfigPills({
    gridOptions,
    levelOptions,
    gridSize,
    difficulty,
    onGridChange,
    onDifficultyChange,
    disabled,
    variant = "default",
}) {
    return (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                {gridOptions.map((opt) => (
                    <MiniPill
                        key={`g-${opt.size}`}
                        variant={variant}
                        label={opt.label}
                        selected={gridSize === opt.size}
                        disabled={disabled || opt.disabled}
                        locked={opt.locked}
                        tooltip={opt.tooltip}
                        onClick={() => onGridChange(opt.size)}
                    />
                ))}
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                {levelOptions.map((opt) => (
                    <MiniPill
                        key={`d-${opt.value}`}
                        variant={variant}
                        label={opt.label}
                        selected={difficulty === opt.value}
                        disabled={disabled || opt.disabled}
                        locked={opt.locked}
                        tooltip={opt.tooltip}
                        onClick={() => onDifficultyChange(opt.value)}
                    />
                ))}
            </Box>
        </Box>
    );
}
